// ═══════════════════════════════════════════
// PeakWave Hierarchical Taxonomy
// Shared by signup form + browse filters
// Organised by industry/function, not season
// ═══════════════════════════════════════════

export interface TreeNode {
  id: string;
  label: string;
  emoji?: string;
  children?: TreeNode[];
}

// ═══ ROLES — Industry → Category → Individual Role ═══

export const ROLES_TREE: TreeNode[] = [
  {
    id: "ski-mountain",
    label: "Ski & Mountain",
    emoji: "⛷️",
    children: [
      { id: "ski-instructor", label: "Ski Instructor" },
      { id: "snowboard-instructor", label: "Snowboard Instructor" },
      { id: "ski-tech", label: "Ski Technician / Boot Fitter" },
      { id: "ski-patrol", label: "Ski Patrol" },
      { id: "lift-operator", label: "Lift Operator" },
      { id: "mountain-guide", label: "Mountain Guide" },
    ],
  },
  {
    id: "yacht",
    label: "Yacht",
    emoji: "⛵",
    children: [
      { id: "yacht-crew", label: "Yacht Crew (General)" },
      {
        id: "yacht-deck",
        label: "Deck",
        children: [
          { id: "deckhand", label: "Deckhand" },
          { id: "bosun", label: "Bosun" },
          { id: "first-officer", label: "First Officer / Mate" },
          { id: "yacht-captain", label: "Captain" },
        ],
      },
      {
        id: "yacht-interior",
        label: "Interior",
        children: [
          { id: "chief-stewardess", label: "Chief Stewardess" },
          { id: "stewardess", label: "Stewardess / Steward" },
          { id: "purser", label: "Purser" },
          { id: "butler", label: "Butler" },
        ],
      },
      {
        id: "yacht-engineering",
        label: "Engineering",
        children: [
          { id: "yacht-engineer", label: "Engineer" },
          { id: "eto", label: "ETO (Electro-Technical Officer)" },
        ],
      },
      {
        id: "yacht-galley",
        label: "Galley",
        children: [
          { id: "yacht-chef", label: "Yacht Chef" },
          { id: "yacht-sous-chef", label: "Sous Chef" },
        ],
      },
      {
        id: "yacht-other",
        label: "Other",
        children: [
          { id: "laundry-crew", label: "Laundry Crew" },
          { id: "onboard-nanny", label: "Onboard Nanny" },
        ],
      },
    ],
  },
  {
    id: "watersports",
    label: "Watersports",
    emoji: "🏄",
    children: [
      { id: "surf-instructor", label: "Surf Instructor" },
      { id: "kitesurf-instructor", label: "Kitesurf Instructor" },
      { id: "windsurf-instructor", label: "Windsurf Instructor" },
      { id: "sailing-instructor", label: "Sailing Instructor" },
      { id: "dive-instructor", label: "Dive Instructor" },
      { id: "sup-instructor", label: "SUP / Paddleboard Instructor" },
      { id: "wakeboard-instructor", label: "Wakeboard Instructor" },
      { id: "kayak-instructor", label: "Kayak / Canoe Instructor" },
      { id: "rafting-guide", label: "Rafting Guide / Instructor" },
      { id: "boat-captain", label: "Boat Captain" },
      { id: "beach-lifeguard", label: "Beach Lifeguard" },
      { id: "watersports-manager", label: "Watersports Centre Manager" },
    ],
  },
  {
    id: "hospitality",
    label: "Hospitality",
    emoji: "🏨",
    children: [
      {
        id: "chalet",
        label: "Chalet",
        children: [
          { id: "chalet-host", label: "Chalet Host" },
          { id: "chalet-chef", label: "Chalet Chef" },
        ],
      },
      {
        id: "kitchen",
        label: "Kitchen",
        children: [
          { id: "chef-cook", label: "Chef / Cook" },
          { id: "sous-chef", label: "Sous Chef" },
          { id: "kitchen-porter", label: "Kitchen Porter" },
        ],
      },
      {
        id: "front-of-house",
        label: "Front of House",
        children: [
          { id: "bar-staff", label: "Bar Staff" },
          { id: "waiting-staff", label: "Waiting Staff" },
          { id: "barista-role", label: "Barista" },
          { id: "receptionist", label: "Receptionist" },
          { id: "night-porter", label: "Night Porter" },
          { id: "concierge", label: "Concierge" },
        ],
      },
      {
        id: "accommodation",
        label: "Accommodation",
        children: [
          { id: "housekeeping", label: "Housekeeping" },
          { id: "hotel-staff", label: "Hotel Staff (General)" },
          { id: "villa-host", label: "Villa Host" },
          { id: "resort-rep", label: "Resort Rep" },
        ],
      },
      { id: "beach-club", label: "Beach Club Staff" },
      { id: "retail-shop", label: "Retail / Shop Staff" },
    ],
  },
  {
    id: "sports-wellness",
    label: "Sports & Wellness",
    emoji: "🏋️",
    children: [
      { id: "tennis-coach", label: "Tennis Coach" },
      { id: "golf-club", label: "Golf Instructor" },
      { id: "yoga-instructor", label: "Yoga Instructor" },
      { id: "pilates-instructor", label: "Pilates Instructor" },
      { id: "personal-trainer", label: "Personal Trainer" },
      { id: "fitness-instructor", label: "Fitness Instructor" },
      { id: "spa-wellness", label: "Spa Therapist" },
      { id: "swimming-instructor", label: "Swimming Instructor" },
    ],
  },
  {
    id: "festivals-events",
    label: "Festivals & Events",
    emoji: "🎪",
    children: [
      { id: "event-manager", label: "Event Manager / Coordinator" },
      { id: "stage-hand", label: "Stage Hand / Crew" },
      { id: "event-marshal", label: "Event Marshal / Steward" },
      { id: "sound-lighting", label: "Sound / Lighting Technician" },
      { id: "catering-vendor", label: "Catering / Food Vendor Staff" },
      { id: "festival-security", label: "Security" },
      { id: "campsite-manager", label: "Campsite Manager" },
      { id: "ticket-guest-services", label: "Ticket / Guest Services" },
      { id: "event-staff", label: "Event Staff (General)" },
      { id: "dj", label: "DJ" },
      { id: "musician", label: "Musician / Live Performer" },
      { id: "singer", label: "Singer / Vocalist" },
    ],
  },
  {
    id: "childcare-roles",
    label: "Childcare",
    emoji: "👶",
    children: [
      { id: "nanny", label: "Nanny" },
      { id: "kids-club", label: "Kids Club Leader" },
      { id: "activity-leader", label: "Activity Leader" },
    ],
  },
  {
    id: "support-transport",
    label: "Support & Transport",
    emoji: "🚐",
    children: [
      { id: "driver", label: "Driver / Transfer Driver" },
      { id: "maintenance", label: "Resort Maintenance" },
    ],
  },
  {
    id: "agriculture",
    label: "Agriculture & Farm Work",
    emoji: "🌾",
    children: [
      { id: "grape-picker", label: "Grape Picker / Vendange" },
      { id: "fruit-picker", label: "Fruit Picker" },
      { id: "farm-hand", label: "Farm Hand" },
      { id: "ranch-hand", label: "Ranch Hand" },
    ],
  },
];

// ═══ LOCATIONS — Region → Country → Resort ═══

export const LOCATIONS_TREE: TreeNode[] = [
  // ─── Winter destinations ───
  {
    id: "european-alps",
    label: "European Alps",
    emoji: "🏔️",
    children: [
      {
        id: "france-alps",
        label: "France",
        emoji: "🇫🇷",
        children: [
          { id: "meribel", label: "Méribel" },
          { id: "val-disere", label: "Val d'Isère" },
          { id: "val-thorens", label: "Val Thorens" },
          { id: "courchevel", label: "Courchevel" },
          { id: "chamonix", label: "Chamonix" },
          { id: "morzine", label: "Morzine" },
          { id: "les-gets", label: "Les Gets" },
          { id: "tignes", label: "Tignes" },
          { id: "les-arcs", label: "Les Arcs" },
          { id: "la-plagne", label: "La Plagne" },
          { id: "avoriaz", label: "Avoriaz" },
          { id: "les-deux-alpes", label: "Les Deux Alpes" },
          { id: "alpe-dhuez", label: "Alpe d'Huez" },
          { id: "la-rosiere", label: "La Rosière" },
          { id: "sainte-foy", label: "Sainte Foy" },
          { id: "megeve", label: "Megève" },
          { id: "flaine", label: "Flaine" },
          { id: "les-menuires", label: "Les Menuires" },
          { id: "saint-martin-de-belleville", label: "Saint Martin de Belleville" },
          { id: "serre-chevalier", label: "Serre Chevalier" },
          { id: "la-clusaz", label: "La Clusaz" },
          { id: "chatel", label: "Châtel" },
        ],
      },
      {
        id: "austria",
        label: "Austria",
        emoji: "🇦🇹",
        children: [
          { id: "st-anton", label: "St Anton" },
          { id: "kitzbuhel", label: "Kitzbühel" },
          { id: "lech", label: "Lech" },
          { id: "ischgl", label: "Ischgl" },
          { id: "mayrhofen", label: "Mayrhofen" },
          { id: "solden", label: "Sölden" },
          { id: "innsbruck", label: "Innsbruck" },
          { id: "zell-am-see", label: "Zell am See" },
          { id: "saalbach", label: "Saalbach" },
          { id: "obertauern", label: "Obertauern" },
          { id: "alpbach", label: "Alpbach" },
          { id: "obergurgl", label: "Obergurgl" },
          { id: "schladming", label: "Schladming" },
          { id: "kaprun", label: "Kaprun" },
        ],
      },
      {
        id: "switzerland",
        label: "Switzerland",
        emoji: "🇨🇭",
        children: [
          { id: "verbier", label: "Verbier" },
          { id: "zermatt", label: "Zermatt" },
          { id: "st-moritz", label: "St Moritz" },
          { id: "laax", label: "Laax" },
          { id: "davos-klosters", label: "Davos / Klosters" },
          { id: "crans-montana", label: "Crans-Montana" },
          { id: "saas-fee", label: "Saas-Fee" },
          { id: "engelberg", label: "Engelberg" },
          { id: "grindelwald", label: "Grindelwald" },
          { id: "wengen", label: "Wengen" },
          { id: "murren", label: "Mürren" },
          { id: "andermatt", label: "Andermatt" },
          { id: "champery", label: "Champéry" },
          { id: "nendaz", label: "Nendaz" },
          { id: "villars", label: "Villars" },
        ],
      },
      {
        id: "italy-alps",
        label: "Italy",
        emoji: "🇮🇹",
        children: [
          { id: "dolomites", label: "Cortina d'Ampezzo / Dolomites" },
          { id: "courmayeur", label: "Courmayeur" },
          { id: "cervinia", label: "Cervinia" },
          { id: "livigno", label: "Livigno" },
          { id: "madonna-di-campiglio", label: "Madonna di Campiglio" },
          { id: "selva-val-gardena", label: "Selva Val Gardena" },
          { id: "bormio", label: "Bormio" },
          { id: "sestriere", label: "Sestriere" },
        ],
      },
      {
        id: "andorra",
        label: "Andorra",
        emoji: "🇦🇩",
        children: [
          { id: "grandvalira", label: "Grandvalira" },
          { id: "vallnord", label: "Vallnord" },
        ],
      },
      {
        id: "germany-alps",
        label: "Germany",
        emoji: "🇩🇪",
        children: [
          { id: "garmisch", label: "Garmisch-Partenkirchen" },
        ],
      },
      {
        id: "spain-alps",
        label: "Spain",
        emoji: "🇪🇸",
        children: [
          { id: "sierra-nevada", label: "Sierra Nevada" },
          { id: "baqueira", label: "Baqueira-Beret" },
        ],
      },
    ],
  },
  {
    id: "north-america",
    label: "North America",
    emoji: "🏔️",
    children: [
      {
        id: "canada",
        label: "Canada",
        emoji: "🇨🇦",
        children: [
          { id: "whistler", label: "Whistler" },
          { id: "banff", label: "Banff" },
          { id: "tremblant", label: "Tremblant" },
          { id: "big-white", label: "Big White" },
          { id: "sun-peaks", label: "Sun Peaks" },
          { id: "revelstoke", label: "Revelstoke" },
          { id: "lake-louise", label: "Lake Louise" },
          { id: "fernie", label: "Fernie" },
          { id: "silver-star", label: "Silver Star" },
        ],
      },
      {
        id: "usa",
        label: "USA",
        emoji: "🇺🇸",
        children: [
          { id: "aspen", label: "Aspen" },
          { id: "vail", label: "Vail" },
          { id: "park-city", label: "Park City" },
          { id: "jackson-hole", label: "Jackson Hole" },
          { id: "mammoth", label: "Mammoth" },
          { id: "breckenridge", label: "Breckenridge" },
          { id: "steamboat", label: "Steamboat" },
          { id: "telluride", label: "Telluride" },
          { id: "deer-valley", label: "Deer Valley" },
          { id: "big-sky", label: "Big Sky" },
          { id: "palisades-tahoe", label: "Palisades Tahoe" },
          { id: "heavenly", label: "Heavenly" },
          { id: "killington", label: "Killington" },
          { id: "stowe", label: "Stowe" },
          { id: "crested-butte", label: "Crested Butte" },
          { id: "sun-valley", label: "Sun Valley" },
          { id: "snowbird", label: "Snowbird" },
          { id: "alta", label: "Alta" },
        ],
      },
    ],
  },
  {
    id: "scandinavia",
    label: "Scandinavia",
    emoji: "🏔️",
    children: [
      {
        id: "norway",
        label: "Norway",
        emoji: "🇳🇴",
        children: [
          { id: "hemsedal", label: "Hemsedal" },
          { id: "trysil", label: "Trysil" },
          { id: "geilo", label: "Geilo" },
          { id: "lofoten", label: "Lofoten" },
        ],
      },
      {
        id: "sweden",
        label: "Sweden",
        emoji: "🇸🇪",
        children: [
          { id: "are", label: "Åre" },
          { id: "salen", label: "Sälen" },
        ],
      },
      {
        id: "finland",
        label: "Finland",
        emoji: "🇫🇮",
        children: [
          { id: "levi", label: "Levi" },
          { id: "ruka", label: "Ruka" },
          { id: "yllas", label: "Ylläs" },
        ],
      },
    ],
  },
  {
    id: "japan",
    label: "Japan",
    emoji: "🏔️",
    children: [
      { id: "niseko", label: "Niseko" },
      { id: "hakuba", label: "Hakuba" },
      { id: "furano", label: "Furano" },
      { id: "myoko", label: "Myoko" },
      { id: "nozawa-onsen", label: "Nozawa Onsen" },
      { id: "rusutsu", label: "Rusutsu" },
      { id: "kiroro", label: "Kiroro" },
      { id: "madarao", label: "Madarao" },
      { id: "shiga-kogen", label: "Shiga Kogen" },
    ],
  },
  {
    id: "southern-hemisphere",
    label: "Southern Hemisphere",
    emoji: "🏔️",
    children: [
      {
        id: "new-zealand",
        label: "New Zealand",
        emoji: "🇳🇿",
        children: [
          { id: "queenstown", label: "Queenstown" },
          { id: "wanaka", label: "Wanaka" },
          { id: "cardrona", label: "Cardrona" },
          { id: "treble-cone", label: "Treble Cone" },
          { id: "mount-hutt", label: "Mount Hutt" },
          { id: "remarkables", label: "The Remarkables" },
          { id: "coronet-peak", label: "Coronet Peak" },
        ],
      },
      {
        id: "australia",
        label: "Australia",
        emoji: "🇦🇺",
        children: [
          { id: "thredbo", label: "Thredbo" },
          { id: "perisher", label: "Perisher" },
          { id: "falls-creek", label: "Falls Creek" },
          { id: "hotham", label: "Hotham" },
        ],
      },
      {
        id: "chile",
        label: "Chile",
        emoji: "🇨🇱",
        children: [
          { id: "portillo", label: "Portillo" },
          { id: "valle-nevado", label: "Valle Nevado" },
          { id: "la-parva", label: "La Parva" },
        ],
      },
      {
        id: "argentina",
        label: "Argentina",
        emoji: "🇦🇷",
        children: [
          { id: "cerro-catedral", label: "Cerro Catedral" },
          { id: "las-lenas", label: "Las Leñas" },
          { id: "cerro-chapelco", label: "Cerro Chapelco" },
        ],
      },
    ],
  },
  {
    id: "eastern-europe",
    label: "Eastern Europe",
    emoji: "🏔️",
    children: [
      {
        id: "bulgaria",
        label: "Bulgaria",
        emoji: "🇧🇬",
        children: [
          { id: "bansko", label: "Bansko" },
          { id: "borovets", label: "Borovets" },
          { id: "pamporovo", label: "Pamporovo" },
        ],
      },
      {
        id: "romania",
        label: "Romania",
        emoji: "🇷🇴",
        children: [
          { id: "poiana-brasov", label: "Poiana Brasov" },
        ],
      },
      {
        id: "slovenia",
        label: "Slovenia",
        emoji: "🇸🇮",
        children: [
          { id: "kranjska-gora", label: "Kranjska Gora" },
        ],
      },
    ],
  },
  // ─── Summer destinations ───
  {
    id: "mediterranean",
    label: "Mediterranean",
    emoji: "☀️",
    children: [
      {
        id: "spain",
        label: "Spain",
        emoji: "🇪🇸",
        children: [
          { id: "mallorca", label: "Mallorca" },
          { id: "ibiza", label: "Ibiza" },
          { id: "menorca", label: "Menorca" },
          { id: "marbella", label: "Marbella" },
          { id: "costa-del-sol", label: "Costa del Sol" },
          { id: "barcelona", label: "Barcelona" },
          { id: "formentera", label: "Formentera" },
          { id: "san-sebastian", label: "San Sebastián" },
          { id: "tenerife", label: "Tenerife" },
          { id: "lanzarote", label: "Lanzarote" },
          { id: "fuerteventura", label: "Fuerteventura" },
          { id: "gran-canaria", label: "Gran Canaria" },
        ],
      },
      {
        id: "greece",
        label: "Greece",
        emoji: "🇬🇷",
        children: [
          { id: "mykonos", label: "Mykonos" },
          { id: "santorini", label: "Santorini" },
          { id: "corfu", label: "Corfu" },
          { id: "crete", label: "Crete" },
          { id: "rhodes", label: "Rhodes" },
          { id: "zakynthos", label: "Zakynthos (Zante)" },
          { id: "kos", label: "Kos" },
          { id: "paros", label: "Paros" },
          { id: "naxos", label: "Naxos" },
          { id: "ios", label: "Ios" },
          { id: "kefalonia", label: "Kefalonia" },
          { id: "lefkada", label: "Lefkada" },
          { id: "skiathos", label: "Skiathos" },
          { id: "paxos", label: "Paxos" },
          { id: "halkidiki", label: "Halkidiki" },
          { id: "athens-coast", label: "Athens Coast" },
          { id: "greek-islands", label: "Other Greek Islands" },
        ],
      },
      {
        id: "france-med",
        label: "France",
        emoji: "🇫🇷",
        children: [
          { id: "antibes", label: "Antibes" },
          { id: "cote-dazur", label: "Saint-Tropez / Côte d'Azur" },
          { id: "monaco", label: "Monaco" },
          { id: "cannes", label: "Cannes" },
          { id: "corsica", label: "Corsica" },
          { id: "provence", label: "Provence" },
          { id: "bordeaux", label: "Bordeaux" },
        ],
      },
      {
        id: "croatia",
        label: "Croatia",
        emoji: "🇭🇷",
        children: [
          { id: "split", label: "Split" },
          { id: "dubrovnik", label: "Dubrovnik" },
          { id: "hvar", label: "Hvar" },
          { id: "dalmatian-coast", label: "Dalmatian Coast (Other)" },
        ],
      },
      {
        id: "turkey",
        label: "Turkey",
        emoji: "🇹🇷",
        children: [
          { id: "bodrum", label: "Bodrum" },
          { id: "fethiye", label: "Fethiye" },
          { id: "gocek", label: "Göcek" },
          { id: "marmaris", label: "Marmaris" },
          { id: "antalya", label: "Antalya" },
        ],
      },
      {
        id: "cyprus",
        label: "Cyprus",
        emoji: "🇨🇾",
        children: [
          { id: "ayia-napa", label: "Ayia Napa" },
          { id: "paphos", label: "Paphos" },
          { id: "limassol", label: "Limassol" },
          { id: "cyprus-island", label: "Cyprus (Other)" },
        ],
      },
      {
        id: "italy-med",
        label: "Italy",
        emoji: "🇮🇹",
        children: [
          { id: "sardinia", label: "Sardinia" },
          { id: "amalfi", label: "Amalfi Coast" },
          { id: "sicily", label: "Sicily" },
          { id: "capri", label: "Capri" },
          { id: "lake-como", label: "Lake Como" },
          { id: "lake-garda", label: "Lake Garda" },
          { id: "puglia", label: "Puglia" },
          { id: "cinque-terre", label: "Cinque Terre" },
        ],
      },
      {
        id: "portugal",
        label: "Portugal",
        emoji: "🇵🇹",
        children: [
          { id: "algarve", label: "Algarve" },
          { id: "lisbon-coast", label: "Lisbon Coast" },
          { id: "madeira", label: "Madeira" },
        ],
      },
      {
        id: "montenegro",
        label: "Montenegro",
        emoji: "🇲🇪",
        children: [
          { id: "budva", label: "Budva" },
          { id: "kotor", label: "Kotor" },
          { id: "tivat", label: "Tivat (Porto Montenegro)" },
          { id: "montenegro-coast", label: "Montenegrin Coast (Other)" },
        ],
      },
      {
        id: "malta",
        label: "Malta",
        emoji: "🇲🇹",
        children: [{ id: "malta-island", label: "Malta" }],
      },
      {
        id: "albania",
        label: "Albania",
        emoji: "🇦🇱",
        children: [{ id: "albanian-riviera", label: "Albanian Riviera" }],
      },
      {
        id: "egypt",
        label: "Egypt",
        emoji: "🇪🇬",
        children: [
          { id: "red-sea", label: "Red Sea Coast" },
          { id: "sharm", label: "Sharm el-Sheikh" },
        ],
      },
    ],
  },
  {
    id: "caribbean-tropics",
    label: "Caribbean",
    emoji: "☀️",
    children: [
      { id: "antigua", label: "Antigua" },
      { id: "bvi", label: "BVI" },
      { id: "usvi", label: "USVI" },
      { id: "st-martin", label: "St Martin" },
      { id: "grenada", label: "Grenada" },
      { id: "st-lucia", label: "St Lucia" },
      { id: "barbados", label: "Barbados" },
      { id: "bahamas", label: "Bahamas" },
      { id: "cayman-islands", label: "Cayman Islands" },
      { id: "turks-caicos", label: "Turks & Caicos" },
      { id: "jamaica", label: "Jamaica" },
      { id: "st-kitts", label: "St Kitts & Nevis" },
      { id: "fort-lauderdale", label: "Fort Lauderdale" },
    ],
  },
  {
    id: "middle-east",
    label: "Middle East & Gulf",
    emoji: "☀️",
    children: [
      {
        id: "uae",
        label: "UAE",
        emoji: "🇦🇪",
        children: [
          { id: "dubai", label: "Dubai" },
          { id: "abu-dhabi", label: "Abu Dhabi" },
          { id: "ras-al-khaimah", label: "Ras Al Khaimah" },
        ],
      },
      {
        id: "oman",
        label: "Oman",
        emoji: "🇴🇲",
        children: [
          { id: "muscat", label: "Muscat" },
          { id: "salalah", label: "Salalah" },
          { id: "musandam", label: "Musandam" },
        ],
      },
      {
        id: "qatar",
        label: "Qatar",
        emoji: "🇶🇦",
        children: [{ id: "doha", label: "Doha" }],
      },
      {
        id: "saudi-arabia",
        label: "Saudi Arabia",
        emoji: "🇸🇦",
        children: [
          { id: "saudi-red-sea", label: "Red Sea / NEOM" },
          { id: "saudi-jeddah", label: "Jeddah" },
          { id: "saudi-riyadh", label: "Riyadh" },
        ],
      },
      {
        id: "bahrain",
        label: "Bahrain",
        emoji: "🇧🇭",
        children: [{ id: "bahrain-manama", label: "Manama" }],
      },
      {
        id: "egypt-red-sea",
        label: "Egypt (Red Sea)",
        emoji: "🇪🇬",
        children: [
          { id: "egypt-hurghada", label: "Hurghada" },
          { id: "egypt-sharm", label: "Sharm El Sheikh" },
          { id: "egypt-el-gouna", label: "El Gouna" },
        ],
      },
      {
        id: "jordan",
        label: "Jordan",
        emoji: "🇯🇴",
        children: [
          { id: "jordan-aqaba", label: "Aqaba" },
          { id: "jordan-dead-sea", label: "Dead Sea Resorts" },
        ],
      },
    ],
  },
  {
    id: "southeast-asia",
    label: "Southeast Asia",
    emoji: "☀️",
    children: [
      {
        id: "thailand",
        label: "Thailand",
        children: [
          { id: "thailand-phuket", label: "Phuket" },
          { id: "thailand-koh-samui", label: "Koh Samui" },
          { id: "thailand-bangkok", label: "Bangkok" },
          { id: "thailand-chiang-mai", label: "Chiang Mai" },
          { id: "thailand-krabi", label: "Krabi & Phi Phi" },
        ],
      },
      {
        id: "indonesia",
        label: "Indonesia",
        children: [
          { id: "bali", label: "Bali" },
          { id: "lombok", label: "Lombok" },
          { id: "gili-islands", label: "Gili Islands" },
          { id: "komodo", label: "Komodo & Flores" },
        ],
      },
      { id: "sri-lanka", label: "Sri Lanka" },
      {
        id: "philippines",
        label: "Philippines",
        children: [
          { id: "philippines-cebu", label: "Cebu" },
          { id: "philippines-palawan", label: "Palawan" },
          { id: "philippines-boracay", label: "Boracay" },
          { id: "philippines-siargao", label: "Siargao" },
        ],
      },
      {
        id: "vietnam",
        label: "Vietnam",
        children: [
          { id: "vietnam-ho-chi-minh", label: "Ho Chi Minh City" },
          { id: "vietnam-da-nang", label: "Da Nang & Hoi An" },
          { id: "vietnam-nha-trang", label: "Nha Trang" },
          { id: "vietnam-phu-quoc", label: "Phu Quoc" },
        ],
      },
      { id: "malaysia", label: "Malaysia" },
      { id: "cambodia", label: "Cambodia" },
      { id: "myanmar", label: "Myanmar" },
    ],
  },
  {
    id: "australia-pacific",
    label: "Australia & Pacific",
    emoji: "☀️",
    children: [
      {
        id: "aus-east-coast",
        label: "Australia East Coast",
        children: [
          { id: "sydney", label: "Sydney" },
          { id: "gold-coast", label: "Gold Coast" },
          { id: "byron-bay", label: "Byron Bay" },
          { id: "sunshine-coast", label: "Sunshine Coast" },
          { id: "whitsundays", label: "Whitsundays" },
          { id: "cairns-reef", label: "Cairns & Great Barrier Reef" },
          { id: "noosa", label: "Noosa" },
          { id: "melbourne", label: "Melbourne" },
        ],
      },
      {
        id: "aus-west-south",
        label: "Australia West & South",
        children: [
          { id: "perth", label: "Perth" },
          { id: "margaret-river", label: "Margaret River" },
          { id: "broome", label: "Broome" },
          { id: "adelaide", label: "Adelaide & Barossa Valley" },
          { id: "tasmania", label: "Tasmania" },
        ],
      },
      {
        id: "aus-outback-tropical",
        label: "Outback & Tropical North",
        children: [
          { id: "darwin", label: "Darwin" },
          { id: "alice-springs", label: "Alice Springs & Uluru" },
          { id: "aus-farm-stations", label: "Farm & Station Work" },
        ],
      },
      {
        id: "new-zealand",
        label: "New Zealand",
        children: [
          { id: "nz-queenstown", label: "Queenstown" },
          { id: "nz-auckland", label: "Auckland" },
          { id: "nz-bay-of-islands", label: "Bay of Islands" },
          { id: "nz-rotorua", label: "Rotorua" },
          { id: "nz-wanaka", label: "Wanaka" },
        ],
      },
      {
        id: "pacific-islands",
        label: "Pacific Islands",
        children: [
          { id: "fiji", label: "Fiji" },
          { id: "tahiti", label: "Tahiti & French Polynesia" },
          { id: "cook-islands", label: "Cook Islands" },
        ],
      },
    ],
  },
  {
    id: "indian-ocean-region",
    label: "Indian Ocean",
    emoji: "☀️",
    children: [
      { id: "maldives", label: "Maldives" },
      { id: "seychelles", label: "Seychelles" },
      { id: "mauritius", label: "Mauritius" },
    ],
  },
  {
    id: "na-summer",
    label: "North America Summer",
    emoji: "☀️",
    children: [
      {
        id: "na-summer-camps",
        label: "Summer Camps",
        children: [
          { id: "na-camp-northeast", label: "Northeast Camps (New England, NY, PA)" },
          { id: "na-camp-southeast", label: "Southeast Camps (NC, VA, GA)" },
          { id: "na-camp-midwest", label: "Midwest Camps (WI, MI, MN)" },
          { id: "na-camp-west", label: "West Coast Camps (CA, OR, WA)" },
        ],
      },
      {
        id: "na-beach-resort",
        label: "Beach & Resort Towns",
        children: [
          { id: "na-cape-cod", label: "Cape Cod" },
          { id: "na-marthas-vineyard", label: "Martha's Vineyard" },
          { id: "na-nantucket", label: "Nantucket" },
          { id: "na-hamptons", label: "The Hamptons" },
          { id: "na-outer-banks", label: "Outer Banks" },
          { id: "na-jersey-shore", label: "Jersey Shore" },
          { id: "na-myrtle-beach", label: "Myrtle Beach" },
          { id: "na-florida-keys", label: "Florida Keys" },
          { id: "na-miami-south-beach", label: "Miami / South Beach" },
          { id: "na-san-diego", label: "San Diego" },
          { id: "na-santa-monica-la", label: "Santa Monica / LA" },
        ],
      },
      {
        id: "na-parks-mountain",
        label: "National Parks & Mountain",
        children: [
          { id: "na-yellowstone", label: "Yellowstone" },
          { id: "na-yosemite", label: "Yosemite" },
          { id: "na-grand-canyon", label: "Grand Canyon" },
          { id: "na-glacier", label: "Glacier National Park" },
          { id: "na-zion", label: "Zion" },
          { id: "na-rocky-mountain", label: "Rocky Mountain National Park" },
          { id: "na-banff-summer", label: "Banff & Lake Louise (Summer)" },
          { id: "na-jackson-hole-summer", label: "Jackson Hole (Summer)" },
          { id: "na-aspen-summer", label: "Aspen (Summer)" },
        ],
      },
      {
        id: "na-alaska",
        label: "Alaska",
        children: [
          { id: "na-anchorage", label: "Anchorage" },
          { id: "na-denali", label: "Denali" },
          { id: "na-fishing-towns", label: "Fishing Towns (Homer, Kodiak, Seward)" },
        ],
      },
      {
        id: "na-hawaii",
        label: "Hawaii",
        children: [
          { id: "na-maui", label: "Maui" },
          { id: "na-oahu", label: "Oahu" },
          { id: "na-big-island", label: "Big Island" },
          { id: "na-kauai", label: "Kauai" },
        ],
      },
      {
        id: "na-canada-summer",
        label: "Canada Summer",
        children: [
          { id: "na-british-columbia", label: "British Columbia (Vancouver, Victoria, Tofino)" },
          { id: "na-ontario", label: "Ontario (Muskoka, Toronto)" },
          { id: "na-alberta-summer", label: "Alberta (Banff, Jasper, Calgary)" },
          { id: "na-quebec", label: "Quebec (Montreal, Quebec City)" },
          { id: "na-maritimes", label: "Maritime Provinces" },
        ],
      },
    ],
  },
  // ─── Festival locations ───
  {
    id: "uk",
    label: "United Kingdom",
    emoji: "🎪",
    children: [
      {
        id: "uk-by-region",
        label: "By Region",
        children: [
          { id: "uk-south-west", label: "South West" },
          { id: "uk-south-east", label: "South East" },
          { id: "uk-london", label: "London" },
          { id: "uk-midlands", label: "Midlands" },
          { id: "uk-north-west", label: "North West" },
          { id: "uk-north-east", label: "North East" },
          { id: "uk-wales", label: "Wales" },
          { id: "uk-scotland", label: "Scotland" },
          { id: "uk-northern-ireland", label: "Northern Ireland" },
        ],
      },
      {
        id: "uk-by-festival",
        label: "By Festival",
        children: [
          { id: "fest-glastonbury", label: "Glastonbury" },
          { id: "fest-reading-leeds", label: "Reading & Leeds" },
          { id: "fest-download", label: "Download" },
          { id: "fest-creamfields", label: "Creamfields" },
          { id: "fest-boomtown", label: "Boomtown" },
          { id: "fest-wireless", label: "Wireless" },
          { id: "fest-isle-of-wight", label: "Isle of Wight Festival" },
          { id: "fest-parklife", label: "Parklife" },
          { id: "fest-trnsmt", label: "TRNSMT" },
          { id: "fest-latitude", label: "Latitude" },
          { id: "fest-all-points-east", label: "All Points East" },
          { id: "fest-bst-hyde-park", label: "BST Hyde Park" },
          { id: "fest-boardmasters", label: "Boardmasters" },
          { id: "fest-camp-bestival", label: "Camp Bestival" },
          { id: "fest-kendal-calling", label: "Kendal Calling" },
          { id: "fest-green-man", label: "Green Man" },
          { id: "fest-tramlines", label: "Tramlines" },
          { id: "fest-lovebox", label: "Lovebox" },
          { id: "fest-lost-village", label: "Lost Village" },
          { id: "fest-labyrinth-on-the-thames", label: "Labyrinth On The Thames" },
          { id: "fest-labyrinth-tofte-manor", label: "Labyrinth Open Air" },
          { id: "fest-shambala", label: "Shambala" },
          { id: "fest-end-of-the-road", label: "End of the Road" },
          { id: "fest-victorious", label: "Victorious" },
          { id: "fest-truck", label: "Truck Festival" },
          { id: "fest-y-not", label: "Y Not Festival" },
          { id: "fest-bloodstock", label: "Bloodstock Open Air" },
          { id: "fest-bearded-theory", label: "Bearded Theory" },
          { id: "fest-beautiful-days", label: "Beautiful Days" },
          { id: "fest-2000trees", label: "2000trees" },
          { id: "fest-slam-dunk-north", label: "Slam Dunk North" },
          { id: "fest-slam-dunk-south", label: "Slam Dunk South" },
          { id: "fest-wilderness", label: "Wilderness" },
        ],
      },
    ],
  },
  {
    id: "europe-festivals",
    label: "Europe",
    emoji: "🎪",
    children: [
      {
        id: "eu-by-country",
        label: "By Country",
        children: [
          { id: "eu-france", label: "France" },
          { id: "eu-spain", label: "Spain" },
          { id: "eu-germany", label: "Germany" },
          { id: "eu-netherlands", label: "Netherlands" },
          { id: "eu-belgium", label: "Belgium" },
          { id: "eu-portugal", label: "Portugal" },
          { id: "eu-croatia", label: "Croatia" },
          { id: "eu-hungary", label: "Hungary" },
          { id: "eu-italy", label: "Italy" },
          { id: "eu-ireland", label: "Ireland" },
          { id: "eu-austria", label: "Austria" },
          { id: "eu-poland", label: "Poland" },
        ],
      },
      {
        id: "eu-by-festival",
        label: "By Festival",
        children: [
          { id: "fest-tomorrowland", label: "Tomorrowland" },
          { id: "fest-primavera-sound", label: "Primavera Sound" },
          { id: "fest-sonar", label: "Sonar" },
          { id: "fest-sziget", label: "Sziget" },
          { id: "fest-hideout", label: "Hideout" },
          { id: "fest-roskilde", label: "Roskilde" },
          { id: "fest-rock-werchter", label: "Rock Werchter" },
          { id: "fest-mysteryland", label: "Mysteryland" },
          { id: "fest-awakenings", label: "Awakenings" },
          { id: "fest-hellfest", label: "Hellfest" },
          { id: "fest-oya", label: "Øya" },
          { id: "fest-lowlands", label: "Lowlands" },
          { id: "fest-electric-picnic", label: "Electric Picnic" },
          { id: "fest-rock-am-ring", label: "Rock am Ring" },
          { id: "fest-rock-im-park", label: "Rock im Park" },
          { id: "fest-wacken", label: "Wacken Open Air" },
          { id: "fest-pukkelpop", label: "Pukkelpop" },
          { id: "fest-hurricane", label: "Hurricane Festival" },
          { id: "fest-southside", label: "Southside Festival" },
          { id: "fest-graspop", label: "Graspop Metal Meeting" },
          { id: "fest-nos-alive", label: "NOS Alive" },
          { id: "fest-mad-cool", label: "Mad Cool" },
          { id: "fest-opener", label: "Open'er Festival" },
          { id: "fest-rock-en-seine", label: "Rock en Seine" },
          { id: "fest-snowbombing", label: "Snowbombing" },
          { id: "fest-snowboxx", label: "Snowboxx" },
          { id: "fest-tomorrowland-winter", label: "Tomorrowland Winter" },
        ],
      },
    ],
  },
  {
    id: "na-festivals",
    label: "North America",
    emoji: "🎪",
    children: [
      { id: "fest-coachella", label: "Coachella" },
      { id: "fest-burning-man", label: "Burning Man" },
      { id: "fest-lollapalooza", label: "Lollapalooza" },
      { id: "fest-edc-las-vegas", label: "EDC Las Vegas" },
      { id: "fest-stagecoach", label: "Stagecoach" },
      { id: "fest-bonnaroo", label: "Bonnaroo" },
      { id: "fest-governors-ball", label: "Governors Ball" },
      { id: "fest-osheaga", label: "Osheaga" },
      { id: "fest-electric-forest", label: "Electric Forest" },
      { id: "fest-austin-city-limits", label: "Austin City Limits" },
      { id: "fest-ultra-miami", label: "Ultra Miami" },
      { id: "fest-shaky-knees", label: "Shaky Knees" },
      { id: "fest-outside-lands", label: "Outside Lands" },
      { id: "fest-shambhala-canada", label: "Shambhala Music Festival" },
    ],
  },
  {
    id: "aus-festivals",
    label: "Australasia",
    emoji: "🎪",
    children: [
      { id: "fest-splendour", label: "Splendour in the Grass" },
      { id: "fest-falls-festival", label: "Falls Festival" },
      { id: "fest-rhythm-vines", label: "Rhythm & Vines" },
      { id: "fest-laneway", label: "Laneway Festival" },
      { id: "fest-beyond-the-valley", label: "Beyond The Valley" },
      { id: "fest-golden-plains", label: "Golden Plains" },
      { id: "fest-meredith", label: "Meredith Music Festival" },
      { id: "fest-woodford-folk", label: "Woodford Folk Festival" },
      { id: "fest-rhythm-and-alps", label: "Rhythm and Alps" },
      { id: "fest-northern-bass", label: "Northern Bass" },
      { id: "fest-womadelaide", label: "WOMADelaide" },
    ],
  },
];

// ═══ QUALIFICATIONS — Category → Qualification ═══

export const QUALIFICATIONS_TREE: TreeNode[] = [
  {
    id: "driving-transport",
    label: "Driving & Transport",
    children: [
      { id: "full-driving-licence", label: "Full Driving Licence" },
      { id: "clean-driving-licence", label: "Clean Driving Licence" },
      { id: "minibus-d1", label: "Minibus (D1)" },
      { id: "trailer-be", label: "Trailer (B+E)" },
    ],
  },
  {
    id: "food-hygiene",
    label: "Food, Beverage & Hygiene",
    children: [
      { id: "food-hygiene-l2", label: "Food Hygiene Level 2" },
      { id: "food-hygiene-l3", label: "Food Hygiene Level 3" },
      { id: "servsafe", label: "ServSafe (US)" },
      { id: "personal-licence", label: "Personal Licence (Alcohol)" },
      { id: "rsa", label: "RSA (Responsible Service of Alcohol)" },
      { id: "chalet-cookery", label: "Chalet Cookery Course" },
      { id: "wset", label: "Wine & Spirits (WSET)" },
      { id: "barista", label: "Barista Training" },
      { id: "silver-service", label: "Silver Service" },
      { id: "cocktail-mixology", label: "Cocktail / Mixology" },
    ],
  },
  {
    id: "ski-snow",
    label: "Ski & Snowboard Instruction",
    children: [
      { id: "basi-l1", label: "BASI Level 1" },
      { id: "basi-l2", label: "BASI Level 2" },
      { id: "basi-l3", label: "BASI Level 3+" },
      { id: "basi-snowboard", label: "BASI Snowboard" },
      { id: "csia", label: "CSIA / CSCF (Canadian)" },
      { id: "psia", label: "PSIA / AASI (American)" },
      { id: "nzsia", label: "NZSIA (New Zealand)" },
      { id: "basi-alpine-l3", label: "BASI Alpine Level 3" },
      { id: "basi-alpine-l4", label: "BASI Alpine Level 4" },
      ...[1, 2, 3, 4].map((level) => ({ id: `basi-snowboard-l${level}`, label: `BASI Snowboard Level ${level}` })),
      ...[1, 2, 3, 4].map((level) => ({ id: `csia-ski-l${level}`, label: `CSIA Ski Level ${level}` })),
      ...[1, 2, 3, 4].map((level) => ({ id: `casi-snowboard-l${level}`, label: `CASI Snowboard Level ${level}` })),
      ...[1, 2, 3].map((level) => ({ id: `psia-alpine-l${level}`, label: `PSIA Alpine Level ${level}` })),
      ...[1, 2, 3].map((level) => ({ id: `aasi-snowboard-l${level}`, label: `AASI Snowboard Level ${level}` })),
      ...[1, 2, 3].map((level) => ({ id: `nzsia-ski-l${level}`, label: `NZSIA Ski Level ${level}` })),
      ...[1, 2, 3].map((level) => ({ id: `sbinz-snowboard-l${level}`, label: `SBINZ Snowboard Level ${level}` })),
      { id: "eurotest", label: "Eurotest" },
      { id: "eurosecurity-2", label: "Eurosecurity 2" },
      {
        id: "french-professional-instructor-card",
        label: "Carte Professionnelle (French Ski Instructor Card)",
      },
      {
        id: "french-european-ski-instructor-certification",
        label: "French or European Ski Instructor Certification",
      },
      { id: "isia", label: "ISIA" },
      { id: "avalanche-safety", label: "Avalanche Safety (AVA)" },
      { id: "mountain-first-aid", label: "Mountain First Aid" },
    ],
  },
  {
    id: "yachting-certs",
    label: "Yachting Certifications",
    children: [
      { id: "stcw", label: "STCW" },
      { id: "stcw-refresher", label: "STCW Refresher" },
      { id: "eng1-medical", label: "ENG1 Medical" },
      { id: "powerboat-l2", label: "Powerboat Level 2" },
      { id: "yachtmaster-offshore", label: "Yachtmaster Offshore" },
      { id: "yachtmaster-coastal", label: "Yachtmaster Coastal" },
      { id: "day-skipper", label: "Day Skipper" },
      { id: "pya", label: "PYA (Professional Yachting Association)" },
      { id: "aec", label: "AEC (Approved Engine Course)" },
      { id: "ship-security-officer", label: "Ship Security Officer (SSO)" },
      { id: "helm", label: "HELM (Leadership & Management)" },
      { id: "superyacht-stewardess", label: "Superyacht Stewardess Course" },
      { id: "interior-management", label: "Interior Management Course" },
    ],
  },
  {
    id: "watersports-certs",
    label: "Watersports Certifications",
    children: [
      { id: "iko-kitesurf", label: "IKO (Kitesurf)" },
      { id: "padi-ssi", label: "PADI / SSI Divemaster" },
      { id: "padi-instructor", label: "PADI / SSI Instructor" },
      { id: "isa-bsa", label: "ISA / BSA (Surf)" },
      { id: "rya-powerboat", label: "RYA Powerboat" },
      { id: "rya-dinghy", label: "RYA Dinghy Instructor" },
      { id: "rya-windsurf", label: "RYA Windsurf Instructor" },
      { id: "beach-lifeguard-cert", label: "Beach Lifeguard (SLSGB / RLSS)" },
      { id: "pool-lifeguard", label: "Pool Lifeguard (NPLQ)" },
      { id: "bwsw", label: "BWSW (Wakeboard / Waterski)" },
      { id: "sup-cert", label: "SUP Instructor Qualification" },
    ],
  },
  {
    id: "safety-security",
    label: "Safety & Security",
    children: [
      { id: "first-aid", label: "First Aid" },
      { id: "paediatric-first-aid", label: "Paediatric First Aid" },
      { id: "dbs-check", label: "DBS / Police Check" },
      { id: "sia-door", label: "SIA Door Supervisor" },
      { id: "sia-cctv", label: "SIA CCTV" },
    ],
  },
  {
    id: "childcare-teaching",
    label: "Childcare & Teaching",
    children: [
      { id: "childcare-qual", label: "Childcare Qualification (CACHE / NVQ)" },
      { id: "ofsted-registered", label: "Ofsted Registered" },
      { id: "tefl", label: "TEFL / CELTA" },
    ],
  },
  {
    id: "hospitality-events",
    label: "Hospitality & Events",
    children: [
      { id: "hospitality-nvq", label: "Hospitality Qualification (NVQ / Diploma)" },
      { id: "event-management", label: "Event Management Qualification" },
      { id: "massage-beauty", label: "Massage / Beauty Therapy (VTCT / ITEC)" },
    ],
  },
];

// ═══ LANGUAGES ═══

export const LANGUAGES = [
  "English",
  "French",
  "German",
  "Spanish",
  "Italian",
  "Portuguese",
  "Dutch",
  "Russian",
  "Japanese",
  "Mandarin",
  "Arabic",
  "Greek",
  "Swedish",
  "Norwegian",
  "Croatian",
  "Thai",
  "Danish",
  "Finnish",
  "Turkish",
  "Indonesian",
  "Korean",
  "Polish",
  "Romanian",
  "Hindi",
] as const;

export const PROFICIENCY_LEVELS = [
  "native",
  "fluent",
  "conversational",
  "basic",
] as const;

export const PROFICIENCY_LABELS: Record<string, string> = {
  native: "Native",
  fluent: "Fluent",
  conversational: "Conversational",
  basic: "Basic",
};

export interface LanguageEntry {
  language: string;
  proficiency: (typeof PROFICIENCY_LEVELS)[number];
}

// ═══ OTHER FILTER OPTIONS ═══

export const EXPERIENCE_LEVELS = [
  { value: "first", label: "First Season" },
  { value: "1-3", label: "1\u20133 Seasons" },
  { value: "4-6", label: "4\u20136 Seasons" },
  { value: "7+", label: "7+ Seasons" },
];

export const VISA_OPTIONS = [
  "UK Citizen",
  "EU Citizen",
  "UK/EU Dual National",
  "Valid EU Work Permit",
  "Valid UK Work Visa",
  "Require Sponsorship",
  "Australian Working Holiday Visa",
  "New Zealand Working Holiday Visa",
  "Other (describe in bio)",
];

export const RIGHT_TO_WORK_REGIONS = [
  "UK",
  "EU / Schengen",
  "Switzerland",
  "USA",
  "Canada",
  "Australia",
  "New Zealand",
  "Middle East (UAE / GCC)",
  "Japan",
];

export interface RightToWorkEntry {
  region: string;
  status: "yes" | "sponsorship";
}

export const LISTING_PERKS = [
  { id: "accommodation", label: "Accommodation Provided", shortLabel: "Accom" },
  { id: "meals", label: "Meals Provided", shortLabel: "Meals" },
  { id: "transport", label: "Transport Provided", shortLabel: "Transport" },
  { id: "lift-pass", label: "Lift Pass Included", shortLabel: "Lift Pass" },
  { id: "season-pass", label: "Season Pass Included", shortLabel: "Season Pass" },
  { id: "ski-hire", label: "Ski/Snowboard Hire Included", shortLabel: "Ski Hire" },
  { id: "staff-discount", label: "Staff Discount", shortLabel: "Discount" },
  { id: "tips", label: "Tips", shortLabel: "Tips" },
  { id: "uniform", label: "Uniform Provided", shortLabel: "Uniform" },
  { id: "training", label: "Training Provided", shortLabel: "Training" },
  { id: "flights", label: "Flights Covered", shortLabel: "Flights" },
  { id: "visa-sponsorship", label: "Visa Sponsorship", shortLabel: "Visa" },
] as const;

export type ListingPerkId = (typeof LISTING_PERKS)[number]["id"];

export function getPerkLabel(id: string): string | undefined {
  return LISTING_PERKS.find((p) => p.id === id)?.label;
}

export function getPerkShortLabel(id: string): string | undefined {
  return LISTING_PERKS.find((p) => p.id === id)?.shortLabel;
}

export const GENDER_OPTIONS = ["Male", "Female", "Prefer not to say"];

export const NATIONALITIES = [
  "British",
  "Irish",
  "French",
  "German",
  "Dutch",
  "Belgian",
  "Swiss",
  "Austrian",
  "Italian",
  "Spanish",
  "Portuguese",
  "Greek",
  "Swedish",
  "Norwegian",
  "Danish",
  "Finnish",
  "Polish",
  "Czech",
  "Romanian",
  "Hungarian",
  "Croatian",
  "Slovenian",
  "Slovak",
  "Bulgarian",
  "Lithuanian",
  "Latvian",
  "Estonian",
  "Luxembourgish",
  "Cypriot",
  "Maltese",
  "American",
  "Canadian",
  "Australian",
  "New Zealander",
  "South African",
  "Brazilian",
  "Argentinian",
  "Mexican",
  "Colombian",
  "Chilean",
  "Peruvian",
  "Japanese",
  "Korean",
  "Chinese",
  "Thai",
  "Filipino",
  "Indian",
  "Pakistani",
  "Nepali",
  "Sri Lankan",
  "Vietnamese",
  "Malaysian",
  "Indonesian",
  "Turkish",
  "Russian",
  "Ukrainian",
  "Israeli",
  "Emirati",
  "Saudi",
  "Qatari",
  "Omani",
  "Moroccan",
  "Egyptian",
  "Kenyan",
  "Nigerian",
  "Ghanaian",
  "Zimbabwean",
  "Other",
];

const INFORMAL_NATIONALITIES: Record<string, string> = {
  "New Zealander": "Kiwi",
  Australian: "Aussie",
  British: "Brit",
};

export function getInformalNationality(nationality: string): string {
  return INFORMAL_NATIONALITIES[nationality] ?? nationality;
}

export const EARLIEST_START_OPTIONS = [
  "Immediately / ASAP",
  "Within 2 weeks",
  "Within 1 month",
  "Specific date (see above)",
];

// ═══ ROLE CATEGORIES FOR BROWSE FILTERING ═══

/** Role leaf IDs grouped by category, for browse ?category= filtering */
export const ROLE_CATEGORIES: Record<string, string[]> = {
  ski: [
    // Ski & Mountain
    "ski-instructor", "snowboard-instructor", "ski-tech", "ski-patrol",
    "lift-operator", "mountain-guide",
    // Chalet
    "chalet-host", "chalet-chef",
  ],
  yacht: [
    "yacht-crew",
    "deckhand", "bosun", "first-officer", "yacht-captain",
    "chief-stewardess", "stewardess", "purser", "butler",
    "yacht-engineer", "eto",
    "yacht-chef", "yacht-sous-chef",
    "laundry-crew", "onboard-nanny",
  ],
  watersports: [
    // Watersports
    "surf-instructor", "kitesurf-instructor", "windsurf-instructor",
    "sailing-instructor", "dive-instructor", "sup-instructor",
    "wakeboard-instructor", "kayak-instructor", "rafting-guide", "boat-captain",
    "beach-lifeguard", "watersports-manager",
    // Sports & Wellness
    "tennis-coach", "golf-club", "yoga-instructor", "pilates-instructor",
    "personal-trainer", "fitness-instructor", "spa-wellness", "swimming-instructor",
  ],
  festivals: [
    "event-manager", "stage-hand", "event-marshal",
    "sound-lighting", "catering-vendor", "festival-security",
    "campsite-manager", "ticket-guest-services", "event-staff", "dj",
    "musician", "singer",
  ],
  agriculture: [
    "grape-picker", "fruit-picker", "farm-hand", "ranch-hand",
  ],
  hospitality: [
    "chalet-host", "chalet-chef",
    "chef-cook", "sous-chef", "kitchen-porter",
    "bar-staff", "waiting-staff", "barista-role", "receptionist",
    "night-porter", "concierge",
    "housekeeping", "hotel-staff", "villa-host", "resort-rep",
    "beach-club", "retail-shop",
  ],
};

// ═══ SEASON-REGION ROLE MAPPING ═══

/** Season region → allowed top-level ROLES_TREE node IDs */
const SEASON_ROLE_CATEGORIES: Record<string, string[]> = {
  // Snow seasons
  "European Alps": ["ski-mountain", "hospitality", "sports-wellness", "childcare-roles", "support-transport"],
  "North America": ["ski-mountain", "hospitality", "sports-wellness", "childcare-roles", "support-transport"],
  "Japan": ["ski-mountain", "hospitality", "sports-wellness", "childcare-roles", "support-transport"],
  "Southern Hemisphere": ["ski-mountain", "hospitality", "sports-wellness", "childcare-roles", "support-transport"],
  "Scandinavia": ["ski-mountain", "hospitality", "sports-wellness", "childcare-roles", "support-transport"],
  // Sun seasons
  "Mediterranean": ["yacht", "watersports", "hospitality", "sports-wellness", "childcare-roles", "support-transport", "agriculture"],
  "Caribbean & Tropics": ["yacht", "watersports", "hospitality", "sports-wellness", "childcare-roles", "support-transport"],
  "North America Summer": ["watersports", "hospitality", "sports-wellness", "childcare-roles", "support-transport", "agriculture"],
  "Middle East & Gulf": ["yacht", "watersports", "hospitality", "sports-wellness", "childcare-roles", "support-transport"],
  "Australia & Pacific": ["watersports", "hospitality", "sports-wellness", "childcare-roles", "support-transport", "agriculture"],
  "Southeast Asia": ["watersports", "hospitality", "sports-wellness", "childcare-roles", "support-transport"],
  // Festival seasons
  "UK Festivals": ["festivals-events", "hospitality", "support-transport", "childcare-roles"],
  "European Festivals": ["festivals-events", "hospitality", "support-transport", "childcare-roles"],
  "North American Festivals": ["festivals-events", "hospitality", "support-transport", "childcare-roles"],
  "Australasia Festivals": ["festivals-events", "hospitality", "support-transport", "childcare-roles"],
};

/** Get all allowed role leaf IDs for a season region */
export function getRolesForSeasonRegion(region: string): string[] | null {
  const allowedNodeIds = SEASON_ROLE_CATEGORIES[region];
  if (!allowedNodeIds) return null;
  const allowedSet = new Set(allowedNodeIds);
  const ids = new Set<string>();
  for (const node of ROLES_TREE) {
    if (allowedSet.has(node.id)) {
      getAllLeafIds(node).forEach((id) => ids.add(id));
    }
  }
  return [...ids];
}

/** Season region → location tree top-level node IDs */
const SEASON_LOCATION_IDS: Record<string, string[]> = {
  "European Alps": ["european-alps", "eastern-europe"],
  "North America": ["north-america"],
  "North America Summer": ["na-summer"],
  "Japan": ["japan"],
  "Southern Hemisphere": ["southern-hemisphere"],
  "Scandinavia": ["scandinavia"],
  "Mediterranean": ["mediterranean"],
  "Middle East & Gulf": ["middle-east"],
  "Caribbean & Tropics": ["caribbean-tropics", "indian-ocean-region"],
  "Australia & Pacific": ["australia-pacific"],
  "Southeast Asia": ["southeast-asia"],
  "UK Festivals": ["uk"],
  "European Festivals": ["europe-festivals"],
  "North American Festivals": ["na-festivals"],
  "Australasia Festivals": ["aus-festivals"],
};

/** Get filtered location tree nodes for a season region */
export function getLocationsForSeasonRegion(region: string): TreeNode[] | null {
  const ids = SEASON_LOCATION_IDS[region];
  if (!ids) return null;
  return LOCATIONS_TREE.filter((node) => ids.includes(node.id));
}

/** Season display group → location tree top-level node IDs */
export const SEASON_GROUP_LOCATION_IDS: Record<string, string[]> = {
  snow: [
    "european-alps", "north-america", "scandinavia",
    "japan", "southern-hemisphere", "eastern-europe",
  ],
  sun: [
    "mediterranean", "caribbean-tropics", "middle-east",
    "southeast-asia", "australia-pacific", "indian-ocean-region", "na-summer",
  ],
  festival: [
    "uk", "europe-festivals", "na-festivals", "aus-festivals",
  ],
};

/** Get filtered location tree nodes for a season display group (snow/sun/festival) */
export function getLocationsForSeasonGroup(group: string): TreeNode[] | null {
  const ids = SEASON_GROUP_LOCATION_IDS[group];
  if (!ids) return null;
  return LOCATIONS_TREE.filter((node) => ids.includes(node.id));
}

// ═══ FESTIVAL → GEOGRAPHIC REGION MAPPING ═══
// Maps each named festival to the geographic sub-region it sits in.
// Used to cross-match the disjoint "By Region" and "By Festival" branches
// under each country so that e.g. selecting "fest-glastonbury" also matches
// candidates who selected "uk-south-west" and vice versa.

const FESTIVAL_REGION_MAP: Record<string, string> = {
  // UK
  "fest-glastonbury": "uk-south-west",
  "fest-reading-leeds": "uk-south-east",
  "fest-download": "uk-midlands",
  "fest-creamfields": "uk-north-west",
  "fest-boomtown": "uk-south-east",
  "fest-wireless": "uk-london",
  "fest-isle-of-wight": "uk-south-east",
  "fest-parklife": "uk-north-west",
  "fest-trnsmt": "uk-scotland",
  "fest-latitude": "uk-south-east",
  "fest-all-points-east": "uk-london",
  "fest-bst-hyde-park": "uk-london",
  "fest-boardmasters": "uk-south-west",
  "fest-camp-bestival": "uk-south-west",
  "fest-kendal-calling": "uk-north-west",
  "fest-green-man": "uk-wales",
  "fest-tramlines": "uk-north-east",
  "fest-lovebox": "uk-london",
  "fest-lost-village": "uk-midlands",
  "fest-labyrinth-on-the-thames": "uk-london",
  "fest-labyrinth-tofte-manor": "uk-south-east",
  // Europe
  "fest-tomorrowland": "eu-belgium",
  "fest-primavera-sound": "eu-spain",
  "fest-sonar": "eu-spain",
  "fest-sziget": "eu-hungary",
  "fest-hideout": "eu-croatia",
  "fest-rock-werchter": "eu-belgium",
  "fest-mysteryland": "eu-netherlands",
  "fest-awakenings": "eu-netherlands",
  "fest-hellfest": "eu-france",
  "fest-lowlands": "eu-netherlands",
  "fest-victorious": "uk-south-east",
  "fest-truck": "uk-south-east",
  "fest-y-not": "uk-midlands",
  "fest-bloodstock": "uk-midlands",
  "fest-bearded-theory": "uk-midlands",
  "fest-beautiful-days": "uk-south-west",
  "fest-2000trees": "uk-south-west",
  "fest-slam-dunk-north": "uk-north-east",
  "fest-slam-dunk-south": "uk-south-east",
  "fest-wilderness": "uk-south-east",
  "fest-electric-picnic": "eu-ireland",
  "fest-rock-am-ring": "eu-germany",
  "fest-rock-im-park": "eu-germany",
  "fest-wacken": "eu-germany",
  "fest-pukkelpop": "eu-belgium",
  "fest-hurricane": "eu-germany",
  "fest-southside": "eu-germany",
  "fest-graspop": "eu-belgium",
  "fest-nos-alive": "eu-portugal",
  "fest-mad-cool": "eu-spain",
  "fest-opener": "eu-poland",
  "fest-rock-en-seine": "eu-france",
  "fest-snowbombing": "eu-austria",
  "fest-snowboxx": "eu-france",
  "fest-tomorrowland-winter": "eu-france",
};

// Reverse map: region → festivals in that region (built once on first use)
let _regionFestivalsMap: Map<string, string[]> | null = null;
function getRegionFestivalsMap(): Map<string, string[]> {
  if (!_regionFestivalsMap) {
    _regionFestivalsMap = new Map();
    for (const [fest, region] of Object.entries(FESTIVAL_REGION_MAP)) {
      const list = _regionFestivalsMap.get(region);
      if (list) list.push(fest);
      else _regionFestivalsMap.set(region, [fest]);
    }
  }
  return _regionFestivalsMap;
}

/**
 * Expands a set of location IDs to cross-match festival and region branches.
 * - Festival IDs gain their geographic region (fest-glastonbury → + uk-south-west)
 * - Region IDs gain festivals in that region (uk-london → + fest-wireless, ...)
 */
export function expandFestivalLocations(locationIds: string[]): string[] {
  if (locationIds.length === 0) return locationIds;
  const expanded = new Set(locationIds);
  const rfMap = getRegionFestivalsMap();
  for (const id of locationIds) {
    const region = FESTIVAL_REGION_MAP[id];
    if (region) expanded.add(region);
    const festivals = rfMap.get(id);
    if (festivals) for (const f of festivals) expanded.add(f);
  }
  if (expanded.size === locationIds.length) return locationIds;
  return Array.from(expanded);
}

/** Map a location leaf ID to its top-level region label */
const _regionCache = new Map<string, string>();
export function getRegionForLocation(locationId: string): string | null {
  if (_regionCache.size === 0) {
    for (const region of LOCATIONS_TREE) {
      for (const leaf of getAllLeafIds(region)) {
        _regionCache.set(leaf, region.label);
      }
    }
  }
  return _regionCache.get(locationId) ?? null;
}

/**
 * Build a filtered roles tree for a season region.
 * Returns the top-level ROLES_TREE nodes that are allowed for the region.
 * Since the tree is now organised by industry (not season), this is a
 * simple filter of top-level nodes.
 */
export function buildFilteredRolesTree(region: string): TreeNode[] | null {
  const allowedNodeIds = SEASON_ROLE_CATEGORIES[region];
  if (!allowedNodeIds) return null;
  const allowedSet = new Set(allowedNodeIds);
  return ROLES_TREE.filter((node) => allowedSet.has(node.id));
}

/**
 * Sub-category-level mapping of suggested roles per season region.
 * Uses node IDs at any depth (top-level, sub-category, or leaf).
 * Nodes listed here appear in the "Suggested" section; everything
 * else in the allowed tree goes into "Other roles".
 */
const SEASON_SUGGESTED_ROLES: Record<string, string[]> = {
  // Snow — ski/mountain + chalet are the core; kitchen/front-of-house/childcare secondary
  "European Alps": ["ski-mountain", "chalet", "kitchen", "front-of-house", "childcare-roles"],
  "North America": ["ski-mountain", "chalet", "kitchen", "front-of-house", "childcare-roles"],
  "Japan": ["ski-mountain", "chalet", "kitchen", "front-of-house", "childcare-roles"],
  "Southern Hemisphere": ["ski-mountain", "chalet", "kitchen", "front-of-house", "childcare-roles"],
  "Scandinavia": ["ski-mountain", "chalet", "kitchen", "front-of-house", "childcare-roles"],
  // Sun — yacht, watersports, beach-club are the core
  "Mediterranean": ["yacht", "watersports", "beach-club", "front-of-house", "kitchen", "sports-wellness"],
  "Caribbean & Tropics": ["yacht", "watersports", "beach-club", "front-of-house", "kitchen"],
  "Middle East & Gulf": ["yacht", "front-of-house", "kitchen", "sports-wellness"],
  "Australia & Pacific": ["watersports", "beach-club", "front-of-house", "kitchen", "sports-wellness"],
  "Southeast Asia": ["watersports", "beach-club", "front-of-house", "kitchen", "sports-wellness"],
  // Festivals — events + bar/catering are the core
  "UK Festivals": ["festivals-events", "bar-staff", "waiting-staff", "barista-role", "kitchen", "support-transport"],
  "European Festivals": ["festivals-events", "bar-staff", "waiting-staff", "barista-role", "kitchen", "support-transport"],
  "North American Festivals": ["festivals-events", "bar-staff", "waiting-staff", "barista-role", "kitchen", "support-transport"],
  "Australasia Festivals": ["festivals-events", "bar-staff", "waiting-staff", "barista-role", "kitchen", "support-transport"],
};

/**
 * Split the allowed roles tree into { suggested, other } based on season context.
 * "Suggested" roles are the most relevant for the season; "other" contains
 * everything else that's allowed but less common.
 *
 * Extraction works at any depth: if a suggested ID matches a top-level node,
 * the whole node moves to suggested. If it matches a sub-category (e.g. "kitchen"
 * inside "hospitality"), that sub-category is extracted and the parent is rebuilt
 * without it (and placed in "other" if it still has remaining children).
 * Leaf-level IDs are extracted similarly.
 *
 * Returns null if the region has no mapping (caller should fall back to full tree).
 */
export function getContextualRoleTrees(
  region: string
): { suggested: TreeNode[]; other: TreeNode[] } | null {
  const allowedTree = buildFilteredRolesTree(region);
  if (!allowedTree) return null;

  const suggestedIds = SEASON_SUGGESTED_ROLES[region];
  if (!suggestedIds) return { suggested: allowedTree, other: [] };

  const suggestedSet = new Set(suggestedIds);
  const suggested: TreeNode[] = [];
  const other: TreeNode[] = [];

  for (const topNode of allowedTree) {
    // Entire top-level node is suggested
    if (suggestedSet.has(topNode.id)) {
      suggested.push(topNode);
      continue;
    }

    // No children — check if this leaf is suggested
    if (!topNode.children || topNode.children.length === 0) {
      if (suggestedSet.has(topNode.id)) {
        suggested.push(topNode);
      } else {
        other.push(topNode);
      }
      continue;
    }

    // Has children — split them
    const suggestedChildren: TreeNode[] = [];
    const otherChildren: TreeNode[] = [];

    for (const child of topNode.children) {
      if (suggestedSet.has(child.id)) {
        suggested.push(child);
      } else if (child.children) {
        // Check if any grandchildren are suggested (leaf-level extraction)
        const sugLeaves: TreeNode[] = [];
        const otherLeaves: TreeNode[] = [];
        for (const leaf of child.children) {
          if (suggestedSet.has(leaf.id)) {
            sugLeaves.push(leaf);
          } else {
            otherLeaves.push(leaf);
          }
        }
        if (sugLeaves.length > 0) {
          // Add suggested leaves directly (unwrapped from parent)
          for (const sl of sugLeaves) suggested.push(sl);
        }
        if (otherLeaves.length > 0) {
          otherChildren.push(
            otherLeaves.length === child.children.length
              ? child
              : { ...child, children: otherLeaves }
          );
        }
      } else {
        otherChildren.push(child);
      }
    }

    if (suggestedChildren.length > 0) {
      suggested.push({ ...topNode, children: suggestedChildren });
    }
    if (otherChildren.length > 0) {
      other.push({ ...topNode, children: otherChildren });
    }
  }

  return { suggested, other };
}

// ═══ TREE UTILITY FUNCTIONS ═══

/** Get all leaf node IDs from a tree node */
export function getAllLeafIds(node: TreeNode): string[] {
  if (!node.children || node.children.length === 0) return [node.id];
  return node.children.flatMap(getAllLeafIds);
}

/** Get all leaf IDs from an array of tree nodes */
export function getAllLeafIdsFromTree(tree: TreeNode[]): string[] {
  return tree.flatMap(getAllLeafIds);
}

/** Determine check state of a node given selected leaf IDs */
export function getNodeCheckState(
  node: TreeNode,
  selected: Set<string>
): "checked" | "unchecked" | "indeterminate" {
  if (!node.children || node.children.length === 0) {
    return selected.has(node.id) ? "checked" : "unchecked";
  }
  const leaves = getAllLeafIds(node);
  const checkedCount = leaves.filter((id) => selected.has(id)).length;
  if (checkedCount === 0) return "unchecked";
  if (checkedCount === leaves.length) return "checked";
  return "indeterminate";
}

/** Count selected leaves under a node */
export function countSelectedLeaves(
  node: TreeNode,
  selected: Set<string>
): number {
  const leaves = getAllLeafIds(node);
  return leaves.filter((id) => selected.has(id)).length;
}

/** Toggle a node: if fully checked, uncheck all; otherwise check all */
export function toggleNode(
  node: TreeNode,
  selected: Set<string>
): Set<string> {
  const next = new Set(selected);
  const leaves = getAllLeafIds(node);
  const state = getNodeCheckState(node, selected);

  if (state === "checked") {
    leaves.forEach((id) => next.delete(id));
  } else {
    leaves.forEach((id) => next.add(id));
  }
  return next;
}

/** Find a leaf label by ID across a tree */
export function findLabelById(
  tree: TreeNode[],
  id: string
): string | undefined {
  for (const node of tree) {
    if (node.id === id) return node.label;
    if (node.children) {
      const found = findLabelById(node.children, id);
      if (found) return found;
    }
  }
  return undefined;
}

// ═══ EU LOCATION CONSTANTS ═══

/** Top-level LOCATIONS_TREE region IDs that are in the EU/EEA */
export const EU_REGION_IDS = ["european-alps", "eastern-europe", "scandinavia", "mediterranean", "europe-festivals"];

/** All EU location IDs (regions + their leaf IDs) */
export const EU_LOCATION_IDS: Set<string> = new Set(
  EU_REGION_IDS.flatMap((regionId) => {
    const node = LOCATIONS_TREE.find((n) => n.id === regionId);
    return node ? [node.id, ...getAllLeafIds(node)] : [];
  })
);

/** Check if any locations in the array are EU locations */
export function hasEuLocations(locations: string[]): boolean {
  return locations.some((loc) => EU_LOCATION_IDS.has(loc));
}

// ═══ ROLE CATEGORY EXPANSION ═══

/**
 * Expand role category IDs (e.g. "ski", "yacht") to their leaf role IDs
 * using ROLE_CATEGORIES. IDs that are already leaf IDs pass through unchanged.
 * Deduplicates the result.
 */
export function expandRoleCategoriesToLeafIds(ids: string[]): string[] {
  const result = new Set<string>();
  for (const id of ids) {
    const categoryLeaves = ROLE_CATEGORIES[id];
    if (categoryLeaves) {
      for (const leaf of categoryLeaves) result.add(leaf);
    } else {
      result.add(id);
    }
  }
  return Array.from(result);
}
